import { useDispatch, useSelector } from 'react-redux';
import { DateFilterType, SelectedFilter } from '../components/Filters/types'; // Asumiendo que has importado este tipo
import { FilterTableKey, setSelectedFilters } from '../redux/filtersSlice';
import { RootState } from '../redux/store';
import { HistoricalTableKey, setCurrentPage } from '../redux/historicalSlice';

interface TableFilterManagerProps {
    tableKey: FilterTableKey;
    historicalKey?: HistoricalTableKey;
    hasPagination: boolean;
    onFiltersChange?: (filters: SelectedFilter[]) => void;
}

export const useTableFilterManager = ({ tableKey, historicalKey, hasPagination, onFiltersChange }: TableFilterManagerProps) => {
    const dispatch = useDispatch();
    
    const selectedFilters = useSelector(
        (state: RootState) => state.filters[tableKey]?.selectedFilters || []
    );

    const applyUpdate = (updatedFilters: SelectedFilter[]) => {
        dispatch(setSelectedFilters({ table: tableKey, filters: updatedFilters }));     
        if (hasPagination)   
            dispatch(setCurrentPage({ table: historicalKey!, page: 1 }));        
        onFiltersChange?.(updatedFilters);
    };

    const handleFilterChange = (name: string, values: string[], description: string) => {
        const existingFilterIndex = selectedFilters.findIndex(filter => filter.name === name);
        let updatedFilters: SelectedFilter[];

        if (existingFilterIndex > -1) {
            if (values.length > 0) {
                updatedFilters = selectedFilters.map((filter, index) =>
                    index === existingFilterIndex ? { ...filter, values, description } : filter
                );
            } else {
                updatedFilters = selectedFilters.filter((_, index) => index !== existingFilterIndex);
            }
        } else {
            updatedFilters = [...selectedFilters, { name, values, description }];
        }

        applyUpdate(updatedFilters);
    };

    const handleRemoveFilter = (name: string, value?: string) => {
        let updatedFilters = [];
        if (value == undefined || value == null) {
            updatedFilters = selectedFilters.filter(f => f.name !== name);
        } else {
            updatedFilters = selectedFilters
                .map(filter => {
                    if (filter.name !== name)
                        return filter;
                    const newValues = filter.values?.filter(val => val !== value);
                    return newValues && newValues.length > 0 ? { ...filter, values: newValues } : null;
                })
                .filter((filter): filter is SelectedFilter => filter !== null);
        }

        applyUpdate(updatedFilters);
    };

    const handleRemoveRelatedFilter = (
        filterName: string,
        relatedFilterName: string,
        value?: string
    ) => {
        const updatedFilters = selectedFilters.map(filter => {
            if (filter.name !== filterName) {
                return filter;
            }

            const relatedFilters = filter.relatedFilters
                ?.map(relatedFilter => {
                    if (relatedFilter.name !== relatedFilterName) {
                        return relatedFilter;
                    }

                    if (value === undefined || value === null) {
                        return null;
                    }

                    const newValues = relatedFilter.values?.filter(
                        relatedValue => relatedValue !== value
                    );

                    if (!newValues || newValues.length === 0) {
                        return null;
                    }

                    return {
                        ...relatedFilter,
                        values: newValues
                    };
                })
                .filter(
                    (relatedFilter): relatedFilter is SelectedFilter =>
                        relatedFilter !== null
                );

            return {
                ...filter,
                relatedFilters:
                    relatedFilters && relatedFilters.length > 0
                        ? relatedFilters
                        : undefined
            };
        });

        applyUpdate(updatedFilters);
    };
    
    const handleDateFilterChange = (name: string, description: string, dateValues: DateFilterType) => {
        const existingFilterIndex = selectedFilters.findIndex(filter => filter.name === name);
        if (existingFilterIndex > -1) {
            if (dateValues.initialDate || dateValues.finalDate) {
                const updatedFilters = selectedFilters.map((filter, index) => {
                    if (index === existingFilterIndex) {
                        return {
                            ...filter,
                            dateValues,
                        };
                    }
                    return filter;
                });
                dispatch(setSelectedFilters({ table: tableKey, filters: updatedFilters }));
            }
            else {
                const updatedFilters = selectedFilters.filter((_, index) => index !== existingFilterIndex);
                dispatch(setSelectedFilters({ table: tableKey, filters: updatedFilters }));
            }
        } else {
            dispatch(setSelectedFilters({ table: tableKey, filters: [...selectedFilters, { name, description, dateValues }] }));
        }

        if (hasPagination)
            dispatch(setCurrentPage({ table: historicalKey!, page: 1 }));
    };

    const handleRemoveDateFilter = (name: string, isInitial: boolean) => {
        const updatedFilters = selectedFilters.map((filter) => {
            if (filter.name === name) {
                const newValues = { initialDate: filter.dateValues?.initialDate, finalDate: filter.dateValues?.finalDate };

                if (isInitial) {
                    newValues.initialDate = undefined;
                } else {
                    newValues.finalDate = undefined;
                }

                if (newValues.initialDate || newValues.finalDate) {
                    return { ...filter, dateValues: newValues };
                }
                return null;
            }
            return filter;
        }).filter((filter): filter is SelectedFilter => filter !== null); // Filtra los elementos `null` para eliminarlos

        applyUpdate(updatedFilters);
    }

    const handleClearAllFilters = () => {
        applyUpdate([]);
    };

    return {
        selectedFilters,
        handleFilterChange,
        handleRemoveFilter,
        handleRemoveRelatedFilter,
        handleDateFilterChange,
        handleRemoveDateFilter,
        handleClearAllFilters,
    };
};